import React, { Fragment, useCallback, useState, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import ChevronLeftIcon from '@atlaskit/icon/glyph/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import Tooltip from '@atlaskit/tooltip';

import { useIntl } from 'src/hooks/intl';
import updateMobileHeaderState from 'src/redux/global/actions/update-mobile-header-state';
import { scrollToAnchor } from 'src/redux/pull-request/actions';
import { getActiveDiff } from 'src/redux/pull-request/selectors';
import { useMatch } from 'src/router/hooks';
import { getFileTreeHrefs } from 'src/selectors/file-tree-selectors';

import { PR_PATH } from '../constants';
import { KeyboardShortcutTooltip } from '../tabs/shortcuts/keyboard-shortcut-tooltip';

import messages from './single-file-navigation.i18n';
import * as styles from './single-file-navigation.style';

interface SingleFileNavigationProps {
  tabIndex?: number;
  appearance?: 'default' | 'subtle';
}

const BaseSingleFileNavigation = ({
  tabIndex = 0,
  appearance = 'default',
}: SingleFileNavigationProps) => {
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isFileSelected, setIsFileSelected] = useState(false);

  const dispatch = useDispatch();
  const intl = useIntl();

  const activeDiff: string = useSelector(getActiveDiff);

  // getFileTreeHrefs will return all file tree hrefs. If filter/sort
  // is enabled, it will return a sorted and filtered list of hrefs
  // based on the current selected filter and sort order
  const fileHrefs: string[] = useSelector(getFileTreeHrefs);

  const { path } = useMatch();
  const isPrPage = path === PR_PATH;

  // when current active diff has been changed by file-tree/other means
  useEffect(() => {
    if (activeDiff) {
      const indexInFileArray = fileHrefs.findIndex(
        href => href.replace(/#/, '') === activeDiff
      );
      setIsFileSelected(false);
      setCurrentFileIndex(Math.max(0, indexInFileArray));
    }
  }, [activeDiff, fileHrefs]);

  // move to appropriate diff based on index of file
  useEffect(() => {
    if (isFileSelected) {
      const currFileHref = fileHrefs[currentFileIndex];
      if (currFileHref) {
        const anchorId = currFileHref.replace(/#/, '');
        dispatch(updateMobileHeaderState('none'));
        dispatch(scrollToAnchor(anchorId));
      }
    }
  }, [currentFileIndex, dispatch, fileHrefs, isFileSelected]);

  const handlePrevClick = useCallback(() => {
    setIsFileSelected(true);
    setCurrentFileIndex(currIndex => Math.max(0, currIndex - 1));
  }, []);

  const handleNextClick = useCallback(() => {
    setIsFileSelected(true);
    setCurrentFileIndex(currIndex =>
      Math.min(fileHrefs.length - 1, currIndex + 1)
    );
  }, [fileHrefs.length]);

  // if there's 0-1 file, we don't need to show the counter or nav buttons
  if (!fileHrefs || fileHrefs.length <= 1) {
    return null;
  }

  const nextButton = (
    <Button
      aria-label={intl.formatMessage(messages.nextFile)}
      appearance={appearance}
      isDisabled={fileHrefs && currentFileIndex === fileHrefs.length - 1}
      onClick={handleNextClick}
      iconBefore={
        <ChevronRightIcon label={intl.formatMessage(messages.nextFile)} />
      }
      tabIndex={tabIndex}
    />
  );

  const prevButton = (
    <Button
      aria-label={intl.formatMessage(messages.previousFile)}
      appearance={appearance}
      isDisabled={currentFileIndex === 0}
      onClick={handlePrevClick}
      iconBefore={
        <ChevronLeftIcon label={intl.formatMessage(messages.previousFile)} />
      }
      tabIndex={tabIndex}
    />
  );

  return (
    <Fragment>
      <styles.FileNumber>
        <FormattedMessage
          {...messages.fileNumber}
          values={{
            current: currentFileIndex + 1,
            total: fileHrefs.length,
          }}
        />
      </styles.FileNumber>
      {isPrPage ? (
        <styles.Container>
          <KeyboardShortcutTooltip text={messages.previousFile} shortcut="k">
            {prevButton}
          </KeyboardShortcutTooltip>
          <KeyboardShortcutTooltip text={messages.nextFile} shortcut="j">
            {nextButton}
          </KeyboardShortcutTooltip>
        </styles.Container>
      ) : (
        <styles.Container>
          <Tooltip content={intl.formatMessage(messages.previousFile)}>
            {prevButton}
          </Tooltip>
          <Tooltip content={intl.formatMessage(messages.nextFile)}>
            {nextButton}
          </Tooltip>
        </styles.Container>
      )}
    </Fragment>
  );
};

export const SingleFileNavigation = React.memo(BaseSingleFileNavigation);
